import { ChevronLeft } from 'lucide-react';
import * as React from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@heidi/design-system/button';
import { Drawer, DrawerContent, DrawerTrigger } from '@heidi/design-system/drawer';
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from '@heidi/design-system/popover';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { TypographyCaption, TypographyP } from '@heidi/design-system/typography';
import type { PatientOutcomeSurface } from '@heidi/patients/contracts';
import { cn, useIsMobile } from '@heidi/utils';

import type { SessionListLinkPopoverHost } from '../host';
import type { SuggestedMatchCandidates } from '../suggested-matches';
import { SessionListLinkPopoverContent } from './session-list-link-popover-content';

interface SessionListLinkPopoverBaseProps {
  tooltipContent?: string;
  popoverAlign?: 'start' | 'center' | 'end';
  popoverSide?: 'top' | 'right' | 'bottom' | 'left';
  popoverAlignOffset?: number;
  popoverSideOffset?: number;
  commandInputPlaceholder?: string;
  showTooltip?: boolean;
  className?: string;
  selectedSessionIds: string[];
  disabled?: boolean;
  patientProfileAppointmentCandidates?: SuggestedMatchCandidates | null;
  host: SessionListLinkPopoverHost;
  /** `patient_linked` surface attribution; omitted → the event fires bare. */
  trackingSurface?: PatientOutcomeSurface;
}

type SessionListLinkPopoverProps = SessionListLinkPopoverBaseProps &
  (
    | {
        trigger: React.ReactNode;
        open?: never;
        onOpenChange?: never;
        anchorElement?: never;
      }
    | {
        /**
         * Controlled open state for app-owned entry points outside a trigger subtree.
         * Opening claims the single-open atom (closes uncontrolled siblings). Closing
         * clears the claim only after this host opened — a closed controlled mount
         * must not dismiss an unrelated open popover. When another affordance claims
         * the atom, this host is notified via `onOpenChange(false)`.
         */
        open: boolean;
        onOpenChange: (open: boolean) => void;
        /** Positions a controlled desktop popover against the invoking element. */
        anchorElement: HTMLElement | null;
        trigger?: never;
      }
  );

/**
 * Find-or-link replacement for `LinkSessionsComboBoxResponsive` used by
 * the session-list link-patient affordance (session-row icon + bulk
 * link footer). Same outer shape — Popover on desktop, Drawer on
 * mobile, optional tooltip on the trigger — wrapping
 * `SessionListLinkPopoverContent`, which renders the shared
 * `PatientSearchCombobox` (the same component the in-session header uses).
 *
 * Suggested-match selection links directly inside the combobox (the
 * review/merge dialog was removed in APP-7134), so this shell only owns
 * the empty-create modal lifecycle.
 *
 * Gated externally on `useIsFindOrLinkPatientEnabled()`; the legacy
 * combobox stays in place for users without the flag.
 */
export function SessionListLinkPopover({
  trigger,
  open: controlledOpen,
  onOpenChange,
  anchorElement,
  tooltipContent,
  popoverAlign,
  popoverSide,
  popoverAlignOffset,
  popoverSideOffset,
  commandInputPlaceholder,
  showTooltip,
  className,
  selectedSessionIds,
  disabled,
  patientProfileAppointmentCandidates,
  trackingSurface,
  host,
}: SessionListLinkPopoverProps) {
  const intl = useIntl();
  const backLabel = intl.formatMessage({
    defaultMessage: 'Back',
    id: 'i2fkjXG6yN',
    description: 'Returns to the previous step of the link-session drawer',
  });
  // Coordinated so only one link popover is open at a time (see
  // useSingleOpenLinkPopover). Controlled hosts claim on open so siblings
  // close; they clear the claim only after they themselves opened; and they
  // close via onOpenChange when another affordance takes the claim.
  const [coordinatedOpen, setCoordinatedOpen] = host.useSingleOpenLinkPopover();
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : coordinatedOpen;
  // Stable identity for Floating UI — a fresh `{ current }` each render thrashs positioning.
  const anchorVirtualRef = React.useRef<HTMLElement | null>(null);
  anchorVirtualRef.current = anchorElement ?? null;
  const controlledHadClaimedRef = React.useRef(false);
  const prevCoordinatedOpenRef = React.useRef(coordinatedOpen);
  const setOpen = React.useCallback(
    (next: boolean) => {
      onOpenChange?.(next);
      if (!isControlled) setCoordinatedOpen(next);
    },
    [isControlled, onOpenChange, setCoordinatedOpen]
  );
  React.useEffect(() => {
    if (!isControlled) return;
    if (controlledOpen) {
      setCoordinatedOpen(true);
      controlledHadClaimedRef.current = true;
      return;
    }
    if (controlledHadClaimedRef.current) {
      setCoordinatedOpen(false);
      controlledHadClaimedRef.current = false;
    }
  }, [controlledOpen, isControlled, setCoordinatedOpen]);
  React.useEffect(() => {
    const lostClaim = prevCoordinatedOpenRef.current && !coordinatedOpen;
    prevCoordinatedOpenRef.current = coordinatedOpen;
    if (!isControlled || !controlledOpen || !lostClaim) return;
    onOpenChange?.(false);
  }, [coordinatedOpen, controlledOpen, isControlled, onOpenChange]);
  // Empty-input "Create new patient" opens the legacy full-metadata
  // modal. Mounted here (outside <Popover>) so closing the popover
  // doesn't unmount the modal mid-open.
  const [createModalOpen, setCreateModalOpen] = React.useState(false);
  const handleRequestEmptyCreate = React.useCallback(() => {
    setCreateModalOpen(true);
  }, []);
  const isMobile = useIsMobile();
  const isConsolidatedSessionsEnabled = host.isConsolidatedSessionsListEnabled();
  const isSidebarPanelEnabled = host.isSidebarPanelEnabled();
  // Suggested matches only make sense for a single initiating session; the
  // bulk bar never shows them.
  const patientProfileAppointmentCandidatesForSelection =
    selectedSessionIds.length === 1 ? patientProfileAppointmentCandidates : null;
  const sidebarPanelOverlayTriggerProps =
    host.getSidebarPanelOverlayTriggerProps(isSidebarPanelEnabled);
  const sidebarPanelOverlayOpenProps = host.getSidebarPanelOverlayOpenProps({
    isSidebarPanelEnabled,
    isOpen: open,
  });

  host.useSidebarPanelOverlayLock(open);

  // Mounted at the wrapper level (sibling to <Popover>/<Drawer>) so it
  // survives the popover closing — see comment above on createModalOpen.
  const createModal = (
    <host.LinkSessionsToPatientModal
      selectedSessionIds={selectedSessionIds}
      open={createModalOpen}
      setOpen={setCreateModalOpen}
    />
  );

  if (!isMobile) {
    const popoverBody = (
      <PopoverContent
        // Width mirrors the in-session combobox dropdown: fits its content
        // (so long typed names in the "Create new patient" footer aren't
        // squeezed into multiple lines) with a 280px floor and 80vw ceiling
        // so it stays readable on narrow viewports.
        className={cn('w-max min-w-[280px] max-w-[80vw] p-0', className)}
        align={popoverAlign || 'start'}
        side={popoverSide || 'bottom'}
        alignOffset={popoverAlignOffset || 0}
        sideOffset={popoverSideOffset || 0}
      >
        <SessionListLinkPopoverContent
          host={host}
          setOpen={setOpen}
          onRequestEmptyCreate={handleRequestEmptyCreate}
          commandInputPlaceholder={commandInputPlaceholder}
          selectedSessionIds={selectedSessionIds}
          disabled={disabled}
          patientProfileAppointmentCandidates={
            patientProfileAppointmentCandidatesForSelection
          }
          trackingSurface={trackingSurface}
        />
      </PopoverContent>
    );

    return (
      <>
        {anchorElement ? (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverAnchor virtualRef={anchorVirtualRef} />
            {popoverBody}
          </Popover>
        ) : showTooltip ? (
          <Popover open={open} onOpenChange={setOpen}>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger
                  className={cn(
                    'flex items-center',
                    isConsolidatedSessionsEnabled ? 'shrink-0' : 'w-full'
                  )}
                  asChild
                >
                  <PopoverTrigger
                    {...sidebarPanelOverlayTriggerProps}
                    {...sidebarPanelOverlayOpenProps}
                    asChild
                    disabled={disabled}
                    className={cn(!isConsolidatedSessionsEnabled && 'w-full')}
                  >
                    {trigger}
                  </PopoverTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <TypographyCaption>{tooltipContent}</TypographyCaption>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
              {popoverBody}
            </TooltipProvider>
          </Popover>
        ) : (
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger
              {...sidebarPanelOverlayTriggerProps}
              {...sidebarPanelOverlayOpenProps}
              className={cn(isConsolidatedSessionsEnabled ? 'shrink-0' : 'w-full')}
              asChild
              disabled={disabled}
            >
              {trigger}
            </PopoverTrigger>
            {popoverBody}
          </Popover>
        )}
        {createModal}
      </>
    );
  }

  return (
    <>
      <Drawer open={open} onOpenChange={setOpen}>
        {anchorElement ? null : (
          <DrawerTrigger
            {...sidebarPanelOverlayTriggerProps}
            {...sidebarPanelOverlayOpenProps}
            asChild
            disabled={disabled}
          >
            {trigger}
          </DrawerTrigger>
        )}
        <DrawerContent className="flex w-full gap-3 p-2">
          <Button
            size="lg"
            variant="ghost"
            disableAutomaticSvgSizing
            aria-label={backLabel}
            className="absolute start-2 top-2 size-fit gap-0.5 p-0 text-text-active hover:text-text-active active:text-text-active"
            onClick={() => setOpen(false)}
          >
            <ChevronLeft size={18} className="rtl:-scale-x-100" />
            <TypographyP>{backLabel}</TypographyP>
          </Button>
          <SessionListLinkPopoverContent
            host={host}
            setOpen={setOpen}
            onRequestEmptyCreate={handleRequestEmptyCreate}
            commandInputPlaceholder={commandInputPlaceholder}
            selectedSessionIds={selectedSessionIds}
            disabled={disabled}
            patientProfileAppointmentCandidates={
              patientProfileAppointmentCandidatesForSelection
            }
            trackingSurface={trackingSurface}
          />
        </DrawerContent>
      </Drawer>
      {createModal}
    </>
  );
}
